import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Box, Paper, Typography, Button, Container, AppBar, Toolbar, Link, CircularProgress, Fade } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import GoogleIcon from '@mui/icons-material/Google';

export default function Login() {
  const { loginWithGoogle } = useAuth();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  
  const handleLogin = async () => {
    setIsAuthenticating(true);
    try {
      await loginWithGoogle();
      // No need to setIsAuthenticating(false) here as the component will unmount on successful auth
    } catch (error) {
      console.error('Authentication failed:', error);
      setIsAuthenticating(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" color="transparent" elevation={0} sx={{ bgcolor: 'rgba(25, 118, 210, 0.95)' }}>
        <Toolbar>
          <Box display="flex" alignItems="center">
            <img 
              src="/logo192.png" 
              alt="ThinkGPT Logo" 
              style={{ height: 40, marginRight: 10 }} 
            />
            <Typography variant="h5" color="white" fontWeight="bold">
              ThinkGPT
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)',
          backgroundSize: 'cover',
          position: 'relative',
          overflow: 'hidden',
          p: 2,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100%\' height=\'100%\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z\' fill=\'%23ffffff\' fill-opacity=\'0.1\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")',
            backgroundSize: 'cover',
            opacity: 0.3,
            zIndex: 0
          }
        }}
      >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Paper
            elevation={8}
            sx={{
              p: 5,
              borderRadius: 2,
              textAlign: 'center',
              backdropFilter: 'blur(10px)',
              bgcolor: 'rgba(255,255,255,0.9)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
              transition: 'transform 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-5px)'
              }
            }}
          >
            <Box sx={{ mb: 4 }}>
              <img 
                src="/logo192.png" 
                alt="ThinkGPT Logo" 
                style={{ height: 80, marginBottom: 16 }} 
              />
            </Box>
            <Typography variant="h4" gutterBottom fontWeight="bold" color="primary">
              Welcome to ThinkGPT
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              Your AI-powered bookmark manager for AI conversations.
              <br></br>
              Sign in to organize and access your valuable AI insights.
            </Typography>
            {isAuthenticating ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2 }}>
                <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                  <CircularProgress size={60} thickness={4} />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Fade in={true} style={{ transitionDelay: '800ms' }}>
                      <GoogleIcon color="primary" />
                    </Fade>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mt: 2, color: 'primary.main', fontWeight: 'medium' }}>
                  Authenticating...
                </Typography>
              </Box>
            ) : (
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleLogin} 
                size="large"
                sx={{ 
                  mt: 2, 
                  py: 1.5, 
                  px: 4, 
                  borderRadius: 2,
                  fontWeight: 'bold',
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.6)'
                  }
                }}
                startIcon={<GoogleIcon />}
              >
                Sign in with Google
              </Button>
            )}
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Box 
        component="footer" 
        sx={{ 
          py: 3, 
          px: 2, 
          mt: 'auto', 
          backgroundColor: 'rgba(0, 0, 0, 0.87)',
          color: 'white'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
              © {new Date().getFullYear()} ThinkGPT. All rights reserved.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Link href="https://www.instagram.com/thinkodeai/" target="_blank" color="inherit" aria-label="Instagram" sx={{ display: 'flex', alignItems: 'center' }}>
                <InstagramIcon fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>@thinkodeai</Typography>
              </Link>
              <Link href="https://www.youtube.com/@thinkodeAI" target="_blank" color="inherit" aria-label="YouTube" sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                <YouTubeIcon fontSize="small" sx={{ mr: 0.5 }} />
                <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' } }}>@thinkodeAI</Typography>
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
